import { typeValidator } from '@chilipiper/utils'
import * as R from 'remeda'
import React, { ReactNode, createContext, useEffect, useMemo } from 'react'
import { ListKeyboardDelegate, Key } from 'react-aria'
import { Collection, ListState, Node, useListState } from 'react-stately'
import { SelectListBase } from '../select-list/SelectListBase'
import { useSelectListData } from '../select-list/useSelectListData'
import { useSelectListKeyboardDelegate } from '../select-list/useSelectListKeyboardDelegate'
import { CascadeSelectItem, SelectItem, SelectSection } from '../select/PublicComponents'
import { Skeleton, SkeletonLine } from '../../../../old'
import { Box, Flex, Text } from '../../../core-components'
import { AlertIcon } from '../../data/alert-icon/AlertIcon'
import { SelectItemDataProvider } from './SelectItemContext'
import { DynamicCascadeItem, DynamicItem, dynamicItemToItem } from './dynamicItemUtils'
import { useCascadeSelectState } from './useCascaseSelectState'

type Props<Value extends string | string[]> = {
  children: ReactNode
  eventsRef?: (
    data: {
      collection: Collection<Node<DynamicItem>>
      keyboardDelegate: ListKeyboardDelegate<unknown>
      selectionManager: ListState<unknown>['selectionManager']
    },
    path: string
  ) => () => void
  onClose: () => void
  onFocusItem: (key: Key) => void
  onNextList: () => void
  onPrevList: () => void
  onSelectCascadeItem: (item: DynamicCascadeItem) => void
  path: string
  search?: string
  useVirtualFocus?: boolean
  useVirtualScroll?: boolean
  width?: number
  onChange: (value: Value) => void
  value: Value
}

type DynamicCascadeListContextValue<Value extends string | string[] = string | string[]> = Omit<
  Props<Value>,
  'children'
>

export const DynamicCascadeListContext = createContext<DynamicCascadeListContextValue | undefined>(
  undefined
)

export const CascadeItemSelectList = <Value extends string | string[]>({
  onChange,
  value,
  onSelectCascadeItem,
  children,
  onClose,
  eventsRef,
  path,
  onPrevList,
  onNextList,
  onFocusItem,
  width,
  useVirtualScroll,
  useVirtualFocus,
  search,
}: Props<Value>) => {
  const childrenArray = React.Children.toArray(children)
  const propsWithoutChildren = useMemo(
    () =>
      ({
        value,
        onChange,
        onSelectCascadeItem,
        onFocusItem,
        onClose,
        eventsRef,
        path,
        onPrevList,
        onNextList,
        search,
        useVirtualScroll,
        useVirtualFocus,
        width,
      }) as DynamicCascadeListContextValue<Value>,
    [
      value,
      onChange,
      onSelectCascadeItem,
      onFocusItem,
      onClose,
      eventsRef,
      path,
      onPrevList,
      onNextList,
      search,
      useVirtualScroll,
      useVirtualFocus,
      width,
    ]
  )
  if (childrenArray.length === 1) {
    const [onlyChild] = childrenArray
    if (!typeValidator(SelectSection, CascadeSelectItem, SelectItem)(onlyChild)) {
      return (
        <DynamicCascadeListContext.Provider
          value={{
            ...propsWithoutChildren,
            onChange: (value: string | string[]) => onChange(value as Value),
          }}
        >
          {onlyChild}
        </DynamicCascadeListContext.Provider>
      )
    }
  }
  return (
    <InternalCascadeItemSelectList<Value> {...propsWithoutChildren}>
      {children}
    </InternalCascadeItemSelectList>
  )
}

export const CascadeItems = (props: { children: ReactNode } | { state: 'loading' | 'error' }) => {
  const contextProps = React.useContext(DynamicCascadeListContext)
  if (!contextProps) {
    throw new Error('CascadeItems must be used inside a CascadeItem child component')
  }
  if ('state' in props) {
    if (props.state === 'loading') {
      return (
        <>
          <Box p={4} boxShadow='border/row'>
            <Skeleton width={272} height={16}>
              <SkeletonLine width={272} height={16} />
            </Skeleton>
          </Box>
          <Box p={4} boxShadow='border/row'>
            <Skeleton width={272} height={16}>
              <SkeletonLine width={272} height={16} />
            </Skeleton>
          </Box>
          <Box p={4} boxShadow='border/row'>
            <Skeleton width={272} height={16}>
              <SkeletonLine width={272} height={16} />
            </Skeleton>
          </Box>
        </>
      )
    } else {
      return (
        <Flex
          m={4}
          borderRadius={10}
          bg='bg/section-emphasis'
          boxShadow='border/section'
          px={6}
          gap={3}
          py={3}
          w={272}
        >
          <AlertIcon variant='caution' size={4} my={1} />
          <Text color='fg/major' textStyle='body-multiline'>
            There was an issue while loading fields.
          </Text>
        </Flex>
      )
    }
  }
  return (
    <InternalCascadeItemSelectList<string | string[]> {...contextProps}>
      {props.children}
    </InternalCascadeItemSelectList>
  )
}

const InternalCascadeItemSelectList = <Value extends string | string[]>({
  value,
  onChange,
  onSelectCascadeItem,
  children,
  onClose,
  eventsRef,
  path,
  onPrevList,
  onNextList,
  onFocusItem,
  width,
  useVirtualScroll,
  useVirtualFocus,
  search,
}: Props<Value>) => {
  const selectionMode = Array.isArray(value) ? 'multiple' : 'single'
  const values = Array.isArray(value) ? value : [value]

  const listData = useSelectListData({ children, search })
  const state = useCascadeSelectState(
    useListState({
      selectedKeys: values.filter(R.isNonNullish),
      selectionMode,
      onSelectionChange: selection => {
        const values = [...selection] as Array<string>

        if (selectionMode === 'single') {
          if (values.length > 0) {
            return onChange(values[0] as Value)
          }
        } else if (selection === 'all') {
          return onChange([...listData.existingValues.values()] as Value)
        } else {
          return onChange(values as Value)
        }
      },
      collection: undefined,
      children: dynamicItemToItem,
      items: listData.options,
      disabledKeys: listData.disabledKeys,
    }),
    onSelectCascadeItem
  )
  const keyboardDelegate = useSelectListKeyboardDelegate(state.collection, state.disabledKeys, true)
  useEffect(
    () =>
      eventsRef?.(
        {
          keyboardDelegate,
          selectionManager: state.selectionManager,
          collection: state.collection,
        },
        path
      ),
    [eventsRef, keyboardDelegate, state.selectionManager, state.collection, path]
  )
  return (
    <SelectItemDataProvider value={listData.itemData}>
      <Box h='100%' data-test-id={`select:list:${path}`}>
        <SelectListBase
          onClose={onClose}
          useVirtualFocus={useVirtualFocus}
          keyboardDelegate={keyboardDelegate}
          isLoading={false}
          state={state}
          variant='nested'
          width={width}
          useVirtualScroll={useVirtualScroll}
          onLeftKey={onPrevList}
          onFocusItem={onFocusItem}
          onRightKey={onNextList}
          search={search}
        />
      </Box>
    </SelectItemDataProvider>
  )
}
